import type { RequestConfig } from "@app/config/axios/http";
import { measureApi, type ApiMeasure } from "@src/api/dictionaries/measures";
import { divisionApi, divisionServices, type ApiDivision } from "@src/api/dictionaries/divisions";
import { stockDocTypesApi } from "@src/api/stock/document/docTypes";
import type { ApiDocType } from "@src/api/stock/document/docTypes";
import { stockObjsServices } from "@src/api/stock/stockObjs";
import type { StockObj, StockObjExtends } from "@src/api/stock/stockObjs";
import { productionKindsApi } from "../productionKinds";
import type { ApiProductionKind } from "../productionKinds";
import { wsDepotCardsApi } from "./api.core";
import type {
    ApiDepotCardMovement,
    ApiDepotCardMovementsFilter,
    ApiWsDepotCard,
    DepotCardMovement,
    DepotCardMovementExtends,
    WsDepotCard,
    WsDepotCardExtends,
    WsDepotCardFilter,
} from "./types";

/**
 * Сервисный слой — карточки УП: обогащение и загрузка движений по карточке.
 */
export const wsDepotCardsServices = {
    ...wsDepotCardsApi,

    /**
     * Загружает карточки УП и обогащает их справочными связями по запросу.
     * Сервис обеспечивает согласованность join-данных в результирующей модели.
     * @param params.flt фильтр карточек
     * @param params.include join-поля {@link WsDepotCardExtends}
     * @param params.includeStockObj join-поля ТМЦ при `include: ["Stockobj"]`
     * @param config опции HTTP
     * @returns массив обогащённых карточек (без пагинации — бэкенд её не отдаёт)
     */
    load: async (
        params: {
            flt?: WsDepotCardFilter;
            include?: Array<keyof WsDepotCardExtends>;
            includeStockObj?: Array<keyof StockObjExtends>;
        },
        config?: RequestConfig,
    ): Promise<WsDepotCard[]> => {
        const { flt, include, includeStockObj } = params;
        const cards = await wsDepotCardsApi.load({ flt }, config);

        if (include == null) {
            return cards.map((card: ApiWsDepotCard) => serializeCard(card));
        }

        const divisionCodes = new Set<number>();
        const sectionCodes = new Set<number>();
        const stockObjCodes = new Set<number>();
        const measCodes = new Set<number>();
        const prodcutionTypeCodes = new Set<number>();

        const includeDivision = include.includes("Division");
        const includeDivisionSection = include.includes("DivisionSection");
        const includeStockobj = include.includes("Stockobj");
        const includeMeasure = include.includes("Measure");
        const includeProdcutionType = include.includes("ProductionType");

        cards.forEach((card: ApiWsDepotCard) => {
            if (includeDivision) divisionCodes.add(card.DivisionCode);
            if (includeDivisionSection) sectionCodes.add(card.SectionCode);
            if (includeStockobj && card.StockobjCode !== 0) stockObjCodes.add(card.StockobjCode);
            if (includeMeasure && card.MeasCode !== 0) measCodes.add(card.MeasCode);
            if (includeProdcutionType && card.ProductionTypeCode !== 0) prodcutionTypeCodes.add(card.ProductionTypeCode);
        });

        const divisions = new Map<number, ApiDivision>();
        const sections = new Map<number, ApiDivision>();
        const stockObjs = new Map<number, StockObj>();
        const measures = new Map<number, ApiMeasure>();
        const productionTypes = new Map<number, ApiProductionKind>();

        const promises: Promise<unknown>[] = [];

        if (includeDivision && divisionCodes.size > 0) {
            const loadedDivisions = divisionApi.load({ flt: { Codes: [...divisionCodes] } }, config);
            void loadedDivisions.then(({ items }) => items.forEach((div) => divisions.set(div.Code, div)));
            promises.push(loadedDivisions);
        }
        if (includeDivisionSection && sectionCodes.size > 0) {
            const loadedDivisionsSection = divisionServices.loadBySectionCodes({ sectionCodes: [...sectionCodes] }, config);
            void loadedDivisionsSection.then((items) =>
                items.forEach((item) => {
                    if (item.SectionCode !== 0) {
                        sections.set(item.SectionCode, item);
                    }
                }),
            );
            promises.push(loadedDivisionsSection);
        }
        if (includeStockobj && stockObjCodes.size > 0) {
            const objs = stockObjsServices.loadByFilter({ flt: { Codes: [...stockObjCodes] }, include: includeStockObj });
            void objs.then((_stockObjs) => _stockObjs.forEach((obj) => stockObjs.set(obj.Code, obj)));
            promises.push(objs);
        }
        if (includeMeasure && measCodes.size > 0) {
            const measuresData = measureApi.load({ flt: { Codes: [...measCodes] } }, config);
            void measuresData.then((meas) => meas.forEach((mea) => measures.set(mea.Code, mea)));
            promises.push(measuresData);
        }
        if (includeProdcutionType && prodcutionTypeCodes.size > 0) {
            const types = productionKindsApi.load({ flt: { Codes: [...prodcutionTypeCodes] } }, config);
            void types.then((loadedTypes) => loadedTypes.forEach((type) => productionTypes.set(type.Code, type)));
            promises.push(types);
        }

        await Promise.all(promises);

        return cards.map<WsDepotCard>((card) =>
            serializeCard(card, {
                Division: includeDivision ? divisions.get(card.DivisionCode) : undefined,
                DivisionSection: includeDivisionSection ? sections.get(card.SectionCode) : undefined,
                Stockobj: includeStockobj ? stockObjs.get(card.StockobjCode) : undefined,
                Measure: includeMeasure ? measures.get(card.MeasCode) : undefined,
                ProductionType: includeProdcutionType ? productionTypes.get(card.ProductionTypeCode) : undefined,
            }),
        );
    },

    /**
     * Возвращает одну карточку УП по фильтру.
     * @param params.flt фильтр (обычно `Codes: [code]`)
     * @param params.include join-поля
     * @param params.includeStockObj join-поля ТМЦ
     * @param config опции HTTP
     * @returns первая карточка или `null`
     */
    loadOne: async (
        params: {
            flt: WsDepotCardFilter;
            include?: Array<keyof WsDepotCardExtends>;
            includeStockObj?: Array<keyof StockObjExtends>;
        },
        config?: RequestConfig,
    ): Promise<WsDepotCard | null> => {
        const items = await wsDepotCardsServices.load(params, config);
        return items[0] ?? null;
    },

    movements: {
        ...wsDepotCardsApi.movements,

        /** Загружает движения карточки и при необходимости обогащает типом документа. */
        load: async (params: {
            flt: ApiDepotCardMovementsFilter;
            include?: Array<keyof DepotCardMovementExtends>;
        }): Promise<DepotCardMovement[]> => {
            const { flt, include } = params;
            const moves = await wsDepotCardsApi.movements.load({ flt });

            if (include == null) {
                return moves;
            }

            const docTypeCodes = new Set<number>();
            const includeDocType = include.includes("DocType");

            moves.forEach((item: ApiDepotCardMovement) => {
                if (includeDocType) docTypeCodes.add(item.DocTypeCode);
            });

            const docTypes = new Map<number, ApiDocType>();

            if (includeDocType && docTypeCodes.size > 0) {
                const types = await stockDocTypesApi.loadAll();
                types.forEach((type) => docTypes.set(type.Code, type));
            }

            return moves.map<DepotCardMovement>((move: ApiDepotCardMovement) => ({
                ...move,
                DocType: docTypes.get(move.DocTypeCode),
            }));
        },
    },
} as const;

function serializeCard(apiData: ApiWsDepotCard, includeData?: Partial<WsDepotCardExtends>): WsDepotCard {
    return {
        ...apiData,
        Division: includeData?.Division,
        DivisionCode: apiData.DivisionCode,
        DivisionSection: includeData?.DivisionSection,
        SectionCode: apiData.SectionCode,
        Stockobj: includeData?.Stockobj,
        Measure: includeData?.Measure,
        ProductionType: includeData?.ProductionType,
    };
}
